/** Facility fields that can identify a target requested by the chat assistant. */
export type FacilitySwitchTarget = {
	id: number;
	alias?: string;
	name?: string;
	intraop_facility_name_alias?: string;
};

/** Deferred acknowledgement to send after the target facility's data is ready. */
export type PendingFacilitySwitchFollowUp = {
	targetFacilityId: number;
	targetFacility: FacilitySwitchTarget;
	requestedLabel: string;
};

/** Facility-scoped option collections useful to the assistant after a switch. */
type FacilitySwitchOption = {
	id: number;
	name: string;
};

export type FacilitySwitchFilterContext = {
	serviceLines?: FacilitySwitchOption[];
	encounterTypes?: FacilitySwitchOption[];
};

/** Normalizes user-facing labels so aliases and names can be compared reliably. */
const normalizeFacilitySwitchLabel = (value: unknown) =>
	String(value ?? '')
		.toLowerCase()
		// Facility labels may arrive HTML-encoded from rendered or persisted content.
		.replace(/&amp;/g, '&')
		.replace(/\s+/g, ' ')
		.trim();

/** Finds a facility whose normalized alias or name exactly matches the requested label. */
export const resolveFacilitySwitchTarget = (
	facilityNameOrAlias: string,
	facilitiesToSearch?: Array<FacilitySwitchTarget | undefined>
) => {
	const requested = normalizeFacilitySwitchLabel(facilityNameOrAlias);
	if (!requested) return undefined;

	return facilitiesToSearch?.find((candidate) => {
		if (!candidate) return false;

		const alias = normalizeFacilitySwitchLabel(candidate.alias ?? candidate.intraop_facility_name_alias);
		const name = normalizeFacilitySwitchLabel(candidate.name);

		return alias === requested || name === requested;
	});
};

export const createFacilitySwitchFollowUp = (
	targetFacility: FacilitySwitchTarget,
	requestedLabel: string
): PendingFacilitySwitchFollowUp => {
	return {
		targetFacilityId: targetFacility.id,
		targetFacility,
		requestedLabel,
	};
};

const escapeXml = (value: string) =>
	value.replace(/[<>&"']/g, (character) => {
		const entities: Record<string, string> = {
			'<': '&lt;',
			'>': '&gt;',
			'&': '&amp;',
			'"': '&quot;',
			"'": '&apos;',
		};
		return entities[character];
	});

const formatOptionNames = (options?: FacilitySwitchOption[]) =>
	options
		?.map(({ name }) => escapeXml(name.trim()))
		.filter(Boolean)
		.join(', ') ?? '';

/** Builds the hidden user-role response using the newly loaded facility context. */
export const formatFacilitySwitchFollowUp = (
	pendingFollowUp: PendingFacilitySwitchFollowUp,
	filterContext: FacilitySwitchFilterContext
) => {
	const { targetFacility, requestedLabel } = pendingFollowUp;
	const name = targetFacility.name?.trim() || requestedLabel.trim();
	const alias = (targetFacility.alias ?? targetFacility.intraop_facility_name_alias)?.trim();
	const aliasLine = alias ? `\n  <alias>${escapeXml(alias)}</alias>` : '';

	return `<tool_response>
Facility switched successfully.
<facility_context>
  <name>${escapeXml(name)}</name>${aliasLine}
  <filters>
    <service_lines>${formatOptionNames(filterContext.serviceLines)}</service_lines>
    <encounter_types>${formatOptionNames(filterContext.encounterTypes)}</encounter_types>
  </filters>
</facility_context>
</tool_response>`;
};

/** Treats an assistant message as complete once it is no longer loading. */
export const isCompletedAssistantMessage = (isLoading?: boolean) => !isLoading;
